import type { MeResponse } from "@lms/shared-types";
import type { Session } from "../auth/auth";
import type { PasskeyRecord } from "../auth/cognito-api";
import { LMS_VERSION } from "../config";

/**
 * What the mock adapter and mock API answer with (VITE_MOCK=1): two
 * personas, so the e2e can walk the Security page as a native user and
 * as a federated one. Nothing here is a real person; the domain is
 * reserved (SEC-08).
 */
const TENANT = { id: "0193f8a0-0000-7000-8000-000000000002", slug: "synthetic", displayName: "Synthetic Sponsor Co.", productName: "Harbor Learn", locale: "en-US", timeZone: "America/Los_Angeles" };

export const MOCK_ME: Record<"apprentice" | "staff-sso", MeResponse> = {
  apprentice: {
    person: { id: "01J9MOCKAPPRENTICE", displayName: "Sam Synthetic", givenName: "Sam", familyName: "Synthetic", email: "sam@synthetic.invalid", status: "active" },
    tenant: TENANT,
    roles: ["apprentice"],
    permissions: [],
    version: LMS_VERSION,
    facts: {},
  },
  "staff-sso": {
    person: { id: "01J9MOCKSTAFF", displayName: "Ada Lovelace", givenName: "Ada", familyName: "Lovelace", email: "ada@synthetic.invalid", status: "active" },
    tenant: TENANT,
    roles: ["program-director"],
    permissions: [],
    identityProvider: "synthetic-entra",
    version: LMS_VERSION,
    facts: {},
  },
};

function meFromSession(session: Session): MeResponse {
  const payload = session.idToken.split(".")[1] ?? "";
  return JSON.parse(atob(payload)) as MeResponse;
}

export async function mockApi<T>(session: Session, path: string, _init?: RequestInit): Promise<T> {
  await new Promise((r) => setTimeout(r, 30));
  const me = meFromSession(session);
  if (path === "/v1/me") return me as T;
  if (path === "/v1/me/security-activity") {
    return {
      signInsAvailable: true,
      signIns: [
        { id: "e1", at: new Date(Date.now() - 3_600_000).toISOString(), type: "SignIn", outcome: "pass", ip: "203.0.113.4", place: "Boise, US", device: "Chrome on Mac" },
        { id: "e2", at: new Date(Date.now() - 86_400_000).toISOString(), type: "SignIn", outcome: "fail", ip: "198.51.100.9", place: "Unknown" },
      ],
    } as T;
  }
  throw new Error(`mock API has no ${path}`);
}

/** Cognito's answers in mock mode: one passkey for this site, one enrolled for another relying party (pitfall 17). */
export const MOCK_PASSKEYS: PasskeyRecord[] = [
  { CredentialId: "pk-1", FriendlyCredentialName: "MacBook Touch ID", AuthenticatorAttachment: "platform", CreatedAt: Math.floor(Date.now() / 1000) - 86_400 * 3, RelyingPartyId: typeof window === "undefined" ? "localhost" : window.location.hostname },
  { CredentialId: "pk-2", FriendlyCredentialName: "Old key", AuthenticatorAttachment: "cross-platform", CreatedAt: Math.floor(Date.now() / 1000) - 86_400 * 40, RelyingPartyId: "old.example" },
];
